import * as React from 'react';
import { LogOut } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { cn } from '../../lib/utils';

export interface UserMenuIdentity {
  name: string;
  email?: string;
  avatarUrl?: string;
  /** Optional secondary line under the name (e.g. role, tenant). */
  subtitle?: string;
}

export interface UserMenuProps {
  user: UserMenuIdentity;
  /** Sign-out handler. Renders a "Sign out" item at the bottom when supplied. */
  onSignOut?: () => void;
  /** Show only the avatar in the trigger (no name beside it). */
  compactTrigger?: boolean;
  /** Dropdown menu items — use UserMenuItem / UserMenuSeparator from this module. */
  children?: React.ReactNode;
  className?: string;
}

/**
 * UserMenu — avatar + name trigger that opens an identity-aware dropdown.
 *
 * Children must be `UserMenuItem` and `UserMenuSeparator` elements (or any
 * other `DropdownMenu*` children) — they render in order between the
 * identity card at the top and the optional sign-out row at the bottom.
 */
export function UserMenu({
  user,
  onSignOut,
  compactTrigger,
  children,
  className,
}: UserMenuProps) {
  const initials = React.useMemo(() => {
    const parts = user.name.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
  }, [user.name]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-2 rounded-full p-0.5 pr-2 transition-colors hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
            compactTrigger && 'pr-0.5',
            className,
          )}
          aria-label="Open user menu"
        >
          <Avatar className="h-7 w-7">
            {user.avatarUrl && (
              <AvatarImage src={user.avatarUrl} alt={user.name} />
            )}
            <AvatarFallback className="bg-foreground text-background">
              {initials}
            </AvatarFallback>
          </Avatar>
          {!compactTrigger && (
            <span className="hidden text-sm font-medium text-foreground md:inline">
              {user.name.split(' ')[0]}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 p-1">
        <div className="flex items-start gap-3 px-2 py-2.5">
          <Avatar className="h-9 w-9">
            {user.avatarUrl && (
              <AvatarImage src={user.avatarUrl} alt={user.name} />
            )}
            <AvatarFallback className="bg-foreground text-background">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-foreground">
              {user.name}
            </div>
            {user.email && (
              <div className="truncate text-xs text-muted-foreground">
                {user.email}
              </div>
            )}
            {user.subtitle && (
              <div className="mt-0.5 truncate text-[11px] uppercase tracking-wider text-muted-foreground">
                {user.subtitle}
              </div>
            )}
          </div>
        </div>
        {children && (
          <>
            <DropdownMenuSeparator />
            {children}
          </>
        )}
        {onSignOut && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => onSignOut()}
              className="text-destructive focus:text-destructive"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Re-export the dropdown primitives so consumers don't have to import from two places.
export {
  DropdownMenuItem as UserMenuItem,
  DropdownMenuLabel as UserMenuLabel,
  DropdownMenuSeparator as UserMenuSeparator,
};
